import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { Account, Vendor } from '../../types/bookkeeping.js';

const PAYMENT_TERMS = ['net0', 'net15', 'net30', 'net45', 'net60'] as const;

const VendorSchema = z.object({
  display_name: z.string().min(1).max(200),
  legal_name: z.string().max(200).optional(),
  tax_id: z.string().max(40).optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(40).optional(),
  default_currency: z.string().length(3),
  default_payment_terms: z.enum(PAYMENT_TERMS).default('net30'),
  default_expense_account_id: z.string().uuid().optional().or(z.literal('')),
  notes: z.string().optional(),
});
type VendorForm = z.infer<typeof VendorSchema>;

function toFormDefaults(v?: Vendor): VendorForm {
  return {
    display_name: v?.display_name ?? '',
    legal_name: v?.legal_name ?? '',
    tax_id: v?.tax_id ?? '',
    email: v?.email ?? '',
    phone: v?.phone ?? '',
    default_currency: v?.default_currency ?? 'USD',
    default_payment_terms: v?.default_payment_terms ?? 'net30',
    default_expense_account_id: v?.default_expense_account_id ?? '',
    notes: v?.notes ?? '',
  };
}

function VendorModal({
  vendor,
  accounts,
  onClose,
  onSaved,
}: {
  vendor?: Vendor;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('bookkeeping');
  const isEdit = !!vendor;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VendorForm>({
    resolver: zodResolver(VendorSchema),
    defaultValues: toFormDefaults(vendor),
  });

  const createMutation = useMutation({
    mutationFn: (input: VendorForm) =>
      api('/bookkeeping/vendors', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          legal_name: input.legal_name || null,
          tax_id: input.tax_id || null,
          email: input.email || null,
          phone: input.phone || null,
          default_expense_account_id: input.default_expense_account_id || null,
          notes: input.notes || null,
        }),
      }),
    onSuccess: onSaved,
  });

  const updateMutation = useMutation({
    mutationFn: (input: VendorForm) =>
      api(`/bookkeeping/vendors/${vendor?.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...input,
          legal_name: input.legal_name || null,
          tax_id: input.tax_id || null,
          email: input.email || null,
          phone: input.phone || null,
          default_expense_account_id: input.default_expense_account_id || null,
          notes: input.notes || null,
        }),
      }),
    onSuccess: onSaved,
  });

  const onSubmit = handleSubmit((data) =>
    isEdit ? updateMutation.mutate(data) : createMutation.mutate(data),
  );

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? t('vendors.editTitle') : t('vendors.addButton')}
        </h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm">{t('vendors.fields.displayName')} *</span>
            <input
              {...register('display_name')}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
            {errors.display_name && (
              <span className="text-xs text-red-600">{errors.display_name.message}</span>
            )}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('vendors.fields.legalName')}</span>
              <input
                {...register('legal_name')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('vendors.fields.taxId')}</span>
              <input
                {...register('tax_id')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('vendors.fields.email')}</span>
              <input
                {...register('email')}
                type="email"
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
              {errors.email && <span className="text-xs text-red-600">{errors.email.message}</span>}
            </label>
            <label className="block">
              <span className="text-sm">{t('vendors.fields.phone')}</span>
              <input
                {...register('phone')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('vendors.fields.currency')} *</span>
              <input
                {...register('default_currency')}
                maxLength={3}
                className="mt-1 w-full rounded border px-3 py-2 text-sm uppercase"
              />
              {errors.default_currency && (
                <span className="text-xs text-red-600">{errors.default_currency.message}</span>
              )}
            </label>
            <label className="block">
              <span className="text-sm">{t('vendors.fields.paymentTerms')}</span>
              <select
                {...register('default_payment_terms')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm"
              >
                {PAYMENT_TERMS.map((term) => (
                  <option key={term} value={term}>
                    {term}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-sm">{t('vendors.fields.defaultExpenseAccount')}</span>
            <select
              {...register('default_expense_account_id')}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            >
              <option value="">—</option>
              {accounts
                .filter((a) => a.type === 'expense')
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">{t('vendors.fields.notes')}</span>
            <textarea
              {...register('notes')}
              rows={2}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
            />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {isEdit ? 'Guardar' : t('vendors.addButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function Vendors() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Vendor | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bookkeeping-vendors', activeOrgId],
    queryFn: () => api<{ vendors: Vendor[] }>('/bookkeeping/vendors'),
    enabled: !!activeOrgId,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: Account[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/vendors/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookkeeping-vendors'] }),
  });

  const handleSaved = () => {
    void qc.invalidateQueries({ queryKey: ['bookkeeping-vendors'] });
    setAddOpen(false);
    setEditTarget(null);
  };

  const vendors = data?.vendors ?? [];
  const accounts = accountsData?.tree ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('vendors.title')}</h1>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          {t('vendors.addButton')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-hidden rounded border bg-white">
          <table className="w-full">
            <thead className="border-b text-left text-sm text-slate-600">
              <tr>
                <th className="p-3">{t('vendors.fields.displayName')}</th>
                <th className="p-3">{t('vendors.fields.email')}</th>
                <th className="p-3">{t('vendors.fields.taxId')}</th>
                <th className="p-3">{t('vendors.fields.currency')}</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-sm text-slate-500">
                    No hay proveedores aún.
                  </td>
                </tr>
              )}
              {vendors.map((v) => (
                <tr key={v.id} className="border-b text-sm last:border-0">
                  <td className="p-3 font-medium">{v.display_name}</td>
                  <td className="p-3 text-slate-600">{v.email ?? '—'}</td>
                  <td className="p-3 text-slate-600">{v.tax_id ?? '—'}</td>
                  <td className="p-3">{v.default_currency}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditTarget(v)}
                      className="mr-2 text-xs text-blue-600 hover:underline"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => deactivate.mutate(v.id)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Desactivar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <VendorModal accounts={accounts} onClose={() => setAddOpen(false)} onSaved={handleSaved} />
      )}
      {editTarget && (
        <VendorModal
          vendor={editTarget}
          accounts={accounts}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
